import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { contentStyles, h1, linkReversed, paragraphSmall } from '@helpers/typography';
import SVGRoundedCorner from '@img/icons/rounded-corner.svg';
import SectionWrapper, { Theme } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import styled from 'styled-components';
import { BreadcrumbsStyles } from '../Global/Breadcrumbs/Breadcrumbs.styles';
import SearchBarStyles from '../SearchBar/SearchBar.styles';
const Container = styled(SectionWrapper) `
  --sectionSpacingTop: 24px;
  --sectionSpacingBottom: 24px;

  color: ${brand.global.white};

  ${BreadcrumbsStyles.BreadcrumbList},
  ${BreadcrumbsStyles.Container} {
    padding: 0 0 3px;
    color: ${brand.global.white};
  }

  ${SectionWrapperStyles.Inner} {
    position: relative;
  }

  &[data-theme=${Theme.White}],
  &:not([data-theme]) {
    color: ${brand.primary.blue};

    ${BreadcrumbsStyles.BreadcrumbList},
    ${BreadcrumbsStyles.Container} {
      color: ${brand.primary.blue};
    }
  }

  ${SearchBarStyles.FormWrapper} {
    margin-top: 28px;
    margin-bottom: 24px;
  }
`;
const HeroInner = styled.div `
  @media ${from(Device.TabletSmall)} {
    display: flex;
    justify-content: space-between;
    column-gap: 32px;
  }
`;
const HeroContent = styled.div `
  max-width: 930px;

  @media ${from(Device.TabletSmall)} {
    ${Container}:not([data-theme]) & {
      max-width: min(740px, calc(100% - 172px)); // iconWrapper width + columnGap.
    }
  }

  @media ${from(Device.Tablet)} {
    &[data-corner-border='true'] {
      ${Container}:not([data-theme]) & {
        max-width: min(740px, calc(100% - 280px)); // iconWrapper width + columnGap.
      }
    }
  }
`;
const RoundedCorner = styled(SVGRoundedCorner) `
  position: absolute;
  width: 77px;
  height: 77px;

  &[data-position='top-left'] {
    top: 0;
    left: 0;
  }

  &[data-position='bottom-left'] {
    bottom: 0;
    left: 0;
    transform: scaleY(-1);
  }

  &[data-position='bottom-right'] {
    bottom: 0;
    right: 0;
    transform: scaleX(-1) rotate(-90deg);
  }
`;
const CategoryTag = styled.span `
  ${paragraphSmall};

  display: inline-block;
  padding: 8px 16px;
  margin: ${fluid(32, 48)} 0 0;
  border-radius: 8px;
  color: ${brand.primary.blue};
  ${fonts.dmSans.bold};
  background-color: ${brand.global.white};

  ${Container}:not([data-theme]) &,
  ${Container}[data-theme=${Theme.White}] & {
    color: ${brand.global.white};
    background-color: ${brand.primary.blue};
  }
`;
const Title = styled.h1 `
  ${h1};

  position: relative;
  margin: 24px 0;
  font-size: ${fluid(48, 64)};
  line-height: 1.1;

  ${Container}:not([data-corner-border]) & {
    max-width: 615px;
  }

  ${Container}[data-corner-border="true"] & {
    padding: ${fluid(52, 64)} ${fluid(44, 78)};
  }
`;
const Description = styled.div `
  ${contentStyles};
`;
const LinkListNav = styled.nav `
  margin-top: 38px;
  display: none;

  &[data-view='mobile'] {
    display: block;
    width: calc(100% + var(--sitePadding));
  }

  @media ${from(Device.TabletSmall)} {
    display: block;

    &[data-view='mobile'] {
      display: none;
    }
  }
`;
const LinkList = styled.ul `
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;

  ${LinkListNav}[data-view='mobile'] & {
    padding-right: var(--sitePadding);
    padding-bottom: 12px;
  }

  @media ${from(Device.TabletSmall)} {
    ${scrollbars};
  }
`;
const LinkListItem = styled.li `
  flex-shrink: 0;

  &:not(:last-child) {
    margin-right: ${fluid(24, 40)};
  }
`;
const LinkListItemLink = styled.a `
  ${linkReversed};
  color: currentColor;
  position: relative;
  text-align: center;

  &::after {
    ${transition('opacity')};
    content: attr(title);
    position: absolute;
    top: 0;
    left: 50%;
    width: 100%;
    transform: translateX(-50%);
    ${fonts.dmSans.regular};
  }

  > span {
    ${transition('opacity, text-decoration-color')};
    ${fonts.dmSans.bold};
    opacity: 0;
    text-decoration: underline;
    text-decoration-color: currentColor;
    text-underline-offset: 8px;
    text-decoration-thickness: 2px;
  }

  &:hover {
    &::after {
      opacity: 0;
    }

    > span {
      opacity: 1;
    }
  }
`;
const IconBlock = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
`;
const IconWrapper = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;

  --gradStart: ${brand.primary.red};
  --gradEnd: ${brand.primary.blue};

  ${Container}[data-theme=${Theme.BlueGradient}] & {
    --gradStart: ${brand.primary.cyan};
  }

  ${Container}[data-theme=${Theme.GreenGradient}] & {
    --gradStart: ${brand.primary.green};
  }

  ${Container}[data-theme=${Theme.OrangeGradient}] & {
    --gradStart: ${brand.primary.orange};
  }

  ${Container}[data-theme=${Theme.PurpleGradient}] & {
    --gradStart: ${brand.primary.purple};
  }

  ${Container}[data-theme=${Theme.RedGradient}] & {
    --gradStart: ${brand.primary.red};
  }

  ${Container}[data-white-icon='true'] & {
    --gradStart: ${brand.global.white};
    --gradEnd: ${brand.global.white};
  }

  ${Container}:not([data-theme]) &,
  ${Container}[data-theme=${Theme.White}] {
    height: 75%;
    min-height: 140px;
    max-height: 248px;
    max-width: 100%;
    aspect-ratio: 1;
    border-radius: 100%;
    background: linear-gradient(270.52deg, var(--gradStart) 14.99%, var(--gradEnd) 84.92%);

    img {
      max-width: 60%;
      filter: invert(1);
    }

    @media ${until(Device.TabletSmall)} {
      min-height: 0;
      max-height: none;
      height: 140px;
      margin-left: auto;
    }
  }

  ${Container}[data-theme]:not([data-theme=${Theme.White}]) & {
    mask-image: ${(props) => `url(${props.$svgUrl})`};
    mask-repeat: no-repeat;
    background: linear-gradient(90deg, var(--gradStart) 14.99%, var(--gradEnd) 84.92%);
    height: 100%;

    img,
    svg {
      opacity: 0;
      min-height: 140px;
      height: 100%;
      max-width: 100%;
    }
  }

  @media ${until(Device.Tablet)} {
    ${Container}[data-corner-border="true"]:not([data-theme]) & {
      max-height: 140px;
    }
  }

  @media ${until(Device.MobileLarge)} {
    &[data-has-description='true'] {
      ${Container}:not([data-theme=${Theme.White}]) & {
        width: 100%;
      }
    }
  }
`;
const HeroBannerInnerStyles = {
    CategoryTag,
    Container,
    Description,
    HeroContent,
    HeroInner,
    IconBlock,
    IconWrapper,
    LinkList,
    LinkListItem,
    LinkListItemLink,
    LinkListNav,
    RoundedCorner,
    Title,
};
export default HeroBannerInnerStyles;
